import { gql, useQuery } from '@apollo/client';
import { ACCOUNT_SEARCH_LIMIT, MIN_ACCOUNT_SEARCH_LENGTH } from '@orn/global/admin/accountSearch.js';
import Tabs from '@orn/shared-web/components/Tabs';
import useDebouncedValue from '@orn/shared-web/hooks/useDebouncedValue';
import { cx } from '@orn/shared-web/utils/cx';
import * as React from 'react';
import { useSearchParams } from 'react-router-dom';

import Button from '../../components/Button';
import Header from '../../components/Header';
import Input from '../../components/Input';
import InspectLink from '../../components/InspectLink';
import KbdHint from '../../components/KbdHint';
import Table from '../../components/Table';
import { CHANNEL_HEALTH_FIELDS } from '../ChannelHealth/channelHealthFields';

import { Absent, Field, FieldList, Timestamp } from './AccountFields';
import { ToolLinks } from './AccountLinks';
import {
  ACCOUNT_TABS,
  MATCH_FIELD_LABELS,
  formatHouseholdName,
  formatPersonName,
  householdOwnerId,
  householdToolLinks,
  isSearchTruncated,
  parseAccountTab,
  withAccountTab,
} from './accountsView';
import BankConnectionsPanel from './BankConnectionsPanel';
import BillingPanel from './BillingPanel';
import DependentsPanel from './DependentsPanel';
import LinkedUsersPanel from './LinkedUsersPanel';
import OpenWorkItemsPanel from './OpenWorkItemsPanel';
import PossessionsPanel from './PossessionsPanel';
import SubscriptionBadge from './SubscriptionBadge';

import type {
  AdminSearchAccountsQuery,
  AdminSearchAccountsQueryVariables,
  AdminGetFamilyAccountQuery,
  AdminGetFamilyAccountQueryVariables,
} from './__generated__/AccountsScene.gql';
import type { AccountTab } from './accountsView';

const ADMIN_SEARCH_ACCOUNTS = gql`
  query AdminSearchAccounts($query: String!) {
    adminSearchAccounts(query: $query) {
      familyId
      familyName
      primaryEmail
      memberCount
      subscriptionStatus
      matchedOn
      createdAt
    }
  }
`;

const ADMIN_GET_FAMILY_ACCOUNT = gql`
  query AdminGetFamilyAccount($familyId: ID!) {
    adminGetFamilyAccount(familyId: $familyId) {
      id
      name
      inboundEmailAddress
      memberCount
      createdAt
      linkedUsers {
        id
        displayName
        firstName
        lastName
        preferredName
        email
        emailVerifiedAt
        phoneNumber
        phoneNumberVerifiedAt
        smsConsentState
        outreachActiveHoursStart
        outreachActiveHoursEnd
        outreachDailyCap
        outreachTimezone
        channelHealth {
          ...ChannelHealthFields
        }
        outreachHistory {
          windowDays
          smsDelivered
          emailDelivered
        }
        recentNotifications {
          outreachId
          deliveredAt
          channel
          deliveryStatus
          deliveryFailureCode
          workItemId
          threadId
          title
          body
          category
          cardAsDelivered
          caregiverOnly
          resolution
          resolvedAt
        }
        googleAuthLinked
        familyRole
        status
        lastLoginAt
        lastInboundMessages {
          channel
          sentAt
        }
      }
      openWorkItems {
        truncated
        totalReturned
        items {
          id
          assigneeUserId
          assigneeName
          title
          body
          category
          caregiverOnly
          dueDate
          nudgeDispatchedAt
          nudgeDispatchedThreadId
          nudgeDismissedAt
          nudgeDueAt
          updatedAt
        }
      }
      dependents {
        id
        displayName
        firstName
        lastName
        preferredName
        dateOfBirth
        email
        phoneNumber
        timezone
        address
        linkedUserId
        relationships {
          caregiverUserId
          caregiverName
          relationship
          relationshipOther
          isStep
          isInLaw
          livesWithCaregiver
        }
        partnerships {
          partnerDependentId
          partnerName
          relationship
          relationshipOther
          liveTogether
        }
      }
      possessions {
        residences {
          id
          owner {
            userId
            dependentId
            name
          }
          residenceType
          residenceTypeOther
          address
          city
          country
          isPrimary
          ownership
          ownershipOther
        }
        vehicles {
          id
          owner {
            userId
            dependentId
            name
          }
          year
          make
          model
          ownership
          ownershipOther
        }
      }
      bankConnections {
        id
        institutionName
        plaidItemId
        linkedByUserId
        linkedByName
        linkedAt
        lastSyncedAt
        historicalSyncComplete
        consentedProducts
        needsReconnection
        stale
        neverSynced
        accounts {
          id
          name
          type
          subtype
          mask
          hiddenAt
        }
      }
      billing {
        stripeCustomerId
        subscriptions {
          id
          status
          interval
          priceId
          stripeSubscriptionId
          currentPeriodEnd
          cancelAtPeriodEnd
          pendingInterval
          pendingIntervalAt
          instrumentSwitchClaimedAt
          createdAt
          updatedAt
        }
        paymentMethods {
          id
          type
          institutionName
          mask
          status
          isDefault
          stripePaymentMethodId
          financialAccountId
          financialAccountName
          createdAt
          updatedAt
          mandates {
            id
            acceptedByUserId
            acceptedByName
            acceptedAt
            mandateTextVersion
            amountDisplay
            firstChargeDisplay
            frequency
            ip
            userAgent
            identityMatchResult
            thirdPartyAck
            revokedAt
            revocationChannel
          }
        }
      }
    }
  }
  ${CHANNEL_HEALTH_FIELDS}
`;

/** Keystrokes settle before the unindexed `ILIKE` scan runs. */
const SEARCH_DEBOUNCE_MS = 250;

/** Lets `/` focus the field when the rail is already open. */
const SEARCH_INPUT_ID = 'accounts-search';

/** Where Esc leaves focus when it folds the rail, so the next Tab starts from the toggle. */
const SEARCH_TOGGLE_ID = 'accounts-search-toggle';

type AccountSearchResult = AdminSearchAccountsQuery['adminSearchAccounts'][number];

/**
 * Accounts admin tool. The operator types a household name, or a member's
 * name, email, or phone number into the left rail; the resolver matches every
 * household on any of them and the rail lists them newest first. Selecting one
 * opens it on the right in five tabs (Household, Users, Banks, Billing, Work
 * items) and writes `?familyId=`, plus `?tab=` off the Household tab, so the
 * view is a link an operator can paste into a ticket. The rail folds away once
 * a household is open, giving its tables the width; the heading's toggle or `/`
 * brings it back, and Esc inside it folds it again.
 *
 * Read-only. Everything an operator might change about a household — deleting
 * an account, resyncing a bank, toggling a gate — already has its own tool
 * with its own confirmation; this one answers "what is true about this
 * household", which is the question none of them answer.
 */
const AccountsScene = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedFamilyId = searchParams.get('familyId');
  const tab = parseAccountTab(searchParams.get('tab'));

  const [query, setQuery] = React.useState('');
  const debouncedQuery = useDebouncedValue(query, SEARCH_DEBOUNCE_MS);
  const isQueryRunnable = debouncedQuery.trim().length >= MIN_ACCOUNT_SEARCH_LENGTH;

  const {
    data: searchData,
    loading: searchLoading,
    error: searchError,
    refetch: refetchSearch,
  } = useQuery<AdminSearchAccountsQuery, AdminSearchAccountsQueryVariables>(ADMIN_SEARCH_ACCOUNTS, {
    variables: { query: debouncedQuery },
    skip: !isQueryRunnable,
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
  });

  const {
    data: familyData,
    loading: familyLoading,
    error: familyError,
    refetch: refetchFamily,
  } = useQuery<AdminGetFamilyAccountQuery, AdminGetFamilyAccountQueryVariables>(ADMIN_GET_FAMILY_ACCOUNT, {
    variables: { familyId: selectedFamilyId ?? '' },
    skip: selectedFamilyId === null,
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
  });

  // Memoized so the keydown effect below re-subscribes when the results
  // change rather than on every render — `?? []` mints a new array each time.
  const results = React.useMemo(() => searchData?.adminSearchAccounts ?? [], [searchData]);
  const family = familyData?.adminGetFamilyAccount ?? null;

  // Opening any household (a result, the palette, Back) folds the rail. Reset
  // during render rather than in an effect, so the rail never paints open first.
  const [isSearchReopened, setIsSearchReopened] = React.useState(false);
  const [railFamilyId, setRailFamilyId] = React.useState(selectedFamilyId);
  if (railFamilyId !== selectedFamilyId) {
    setRailFamilyId(selectedFamilyId);
    setIsSearchReopened(false);
  }
  const isSearchOpen = selectedFamilyId === null || isSearchReopened;
  const openSearch = React.useCallback(() => setIsSearchReopened(true), []);
  const closeSearch = React.useCallback(() => setIsSearchReopened(false), []);

  const selectFamily = React.useCallback(
    (familyId: string) => {
      const next = new URLSearchParams(searchParams);
      next.set('familyId', familyId);
      setSearchParams(next);
      // Picking the household already open is the operator done searching too.
      setIsSearchReopened(false);
    },
    [searchParams, setSearchParams]
  );

  // A tab is view state, not a destination: replace, so Back leaves the
  // household instead of stepping back through its tabs.
  const selectTab = React.useCallback(
    (next: AccountTab) => setSearchParams((params) => withAccountTab(params, next), { replace: true }),
    [setSearchParams]
  );

  const [focusedIndex, setFocusedIndex] = React.useState(0);
  // A new result set invalidates the old position, and an index past the end
  // would leave Enter opening nothing.
  React.useEffect(() => setFocusedIndex(0), [debouncedQuery]);

  // `j` / `k` row navigation and `Enter` to open, per the MOPS keyboard
  // convention, and `/` to search. Keystrokes aimed at a field are left alone —
  // this scene's primary control is a text box, so an unguarded handler would
  // eat the `j` an operator typed into the search.
  React.useEffect(() => {
    const handler = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null;
      if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable)) {
        return;
      }
      if (event.key === '/' && !event.metaKey && !event.ctrlKey && !event.altKey) {
        event.preventDefault();
        // A folded rail mounts with the field focused; an open one is focused here.
        if (isSearchOpen) {
          document.getElementById(SEARCH_INPUT_ID)?.focus();
        } else {
          openSearch();
        }
        return;
      }
      // A folded rail's results are out of sight, so nothing may move through or open them.
      if (!isSearchOpen || results.length === 0) {
        return;
      }
      if (event.key === 'j' || event.key === 'ArrowDown') {
        event.preventDefault();
        setFocusedIndex((i) => Math.min(i + 1, results.length - 1));
      } else if (event.key === 'k' || event.key === 'ArrowUp') {
        event.preventDefault();
        setFocusedIndex((i) => Math.max(i - 1, 0));
      } else if (event.key === 'Enter') {
        // Enter on a focused tab, link, or button is that control's, not the result list's.
        if (target?.closest('[role="tablist"], a, button')) {
          return;
        }
        const focused = results[focusedIndex];
        if (focused) {
          event.preventDefault();
          selectFamily(focused.familyId);
        }
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [focusedIndex, isSearchOpen, openSearch, results, selectFamily]);

  return (
    <div className="bg-bg-primary text-fg-primary flex min-h-screen flex-col">
      <Header />
      <main className="flex flex-1 flex-col gap-4 px-6 py-5">
        <div className="flex items-center gap-4">
          <h1 className="text-sm font-semibold uppercase tracking-widest">Accounts</h1>
          {/* With no household open there is nothing to fold the rail away for. */}
          {selectedFamilyId !== null && (
            <Button
              id={SEARCH_TOGGLE_ID}
              color="secondary"
              size="xs"
              onClick={isSearchOpen ? closeSearch : openSearch}
              aria-expanded={isSearchOpen}
              // `/` opens the rail; with it open, `/` only focuses the field.
              aria-keyshortcuts={isSearchOpen ? undefined : '/'}
            >
              {isSearchOpen ? 'Hide search' : 'Find a household'}
              {!isSearchOpen && <KbdHint>/</KbdHint>}
            </Button>
          )}
        </div>

        <div className="flex flex-1 gap-6">
          {isSearchOpen && (
            <SearchRail
              query={query}
              onQueryChange={setQuery}
              isQueryRunnable={isQueryRunnable}
              isLoading={searchLoading}
              hasError={searchError !== undefined}
              onRetry={() => void refetchSearch()}
              results={results}
              focusedIndex={focusedIndex}
              selectedFamilyId={selectedFamilyId}
              onSelectFamily={selectFamily}
              onClose={selectedFamilyId === null ? undefined : closeSearch}
            />
          )}
          <FamilyPane
            familyId={selectedFamilyId}
            isLoading={familyLoading}
            hasError={familyError !== undefined}
            onRetry={() => void refetchFamily()}
            family={family}
            tab={tab}
            onTabChange={selectTab}
          />
        </div>
      </main>
    </div>
  );
};

interface ISearchRailProps {
  query: string;
  onQueryChange: (value: string) => void;
  isQueryRunnable: boolean;
  isLoading: boolean;
  hasError: boolean;
  onRetry: () => void;
  results: AccountSearchResult[];
  focusedIndex: number;
  selectedFamilyId: string | null;
  onSelectFamily: (familyId: string) => void;
  /** Folds the rail away; absent while no household is open, since the rail is then all there is. */
  onClose?: () => void;
}

const SearchRail = ({
  query,
  onQueryChange,
  isQueryRunnable,
  isLoading,
  hasError,
  onRetry,
  results,
  focusedIndex,
  selectedFamilyId,
  onSelectFamily,
  onClose,
}: ISearchRailProps) => (
  <div
    className="border-border-primary flex w-[32rem] min-w-0 shrink-0 flex-col gap-3 overflow-hidden border-r pr-6"
    onKeyDown={(event) => {
      if (event.key === 'Escape' && onClose !== undefined) {
        event.preventDefault();
        // Before the rail unmounts, so focus lands on the toggle rather than `<body>`.
        document.getElementById(SEARCH_TOGGLE_ID)?.focus();
        onClose();
      }
    }}
  >
    <Input
      id={SEARCH_INPUT_ID}
      label="Find a household"
      placeholder="Household name, member name, email, or phone…"
      value={query}
      onChange={onQueryChange}
      hint={`Matches on any of the four. At least ${MIN_ACCOUNT_SEARCH_LENGTH} characters.`}
      // Mounting beside an open household means the operator just asked for it back.
      autoFocus={onClose !== undefined}
    />

    {!isQueryRunnable && query.trim().length > 0 && (
      <div className="text-fg-tertiary text-xs italic">Keep typing — {MIN_ACCOUNT_SEARCH_LENGTH} characters.</div>
    )}

    {hasError && <QueryError message="The search failed to run." onRetry={onRetry} />}

    {!hasError && isQueryRunnable && isLoading && results.length === 0 && (
      <div className="text-fg-secondary text-xs">Searching…</div>
    )}

    {!hasError && isQueryRunnable && !isLoading && results.length === 0 && (
      <div className="text-fg-tertiary text-xs italic">No household matches that.</div>
    )}

    {results.length > 0 && (
      <div className="flex flex-col gap-2">
        {/* `table-fixed` is load-bearing: a `w-full` auto table still grows past
            its container to fit a long email, which is what let the rail bleed
            over the divider into the detail pane. */}
        <Table className="table-fixed">
          <Table.Header sticky>
            <Table.Row>
              <Table.HeaderCell className="w-[8rem]">Household</Table.HeaderCell>
              <Table.HeaderCell>Email</Table.HeaderCell>
              <Table.HeaderCell className="w-[3.5rem] text-right">Users</Table.HeaderCell>
              <Table.HeaderCell className="w-[6.5rem]">Plan</Table.HeaderCell>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {results.map((result, index) => (
              <SearchResultRow
                key={result.familyId}
                result={result}
                isSelected={result.familyId === selectedFamilyId}
                isFocused={index === focusedIndex}
                onSelect={onSelectFamily}
              />
            ))}
          </Table.Body>
        </Table>
        {isSearchTruncated(results.length) && (
          <div className="text-fg-tertiary text-xs italic">
            Showing the first {ACCOUNT_SEARCH_LIMIT} — narrow the search to see the rest.
          </div>
        )}
      </div>
    )}
  </div>
);

interface ISearchResultRowProps {
  result: AccountSearchResult;
  isSelected: boolean;
  /** Where `j` / `k` currently sits, which is not the same as what is open. */
  isFocused: boolean;
  onSelect: (familyId: string) => void;
}

const SearchResultRow = ({ result, isSelected, isFocused, onSelect }: ISearchResultRowProps) => (
  <Table.Row
    className={cx(
      'cursor-pointer',
      isSelected && 'bg-bg-secondary',
      isFocused && 'outline-fg-brand-primary outline outline-1 -outline-offset-1'
    )}
    aria-selected={isSelected}
    title={`Matched on ${MATCH_FIELD_LABELS[result.matchedOn]}`}
    tabIndex={0}
    onClick={() => onSelect(result.familyId)}
    onKeyDown={(event) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        onSelect(result.familyId);
      }
    }}
  >
    <Table.Cell className="truncate">{result.familyName ?? <Absent>Unnamed</Absent>}</Table.Cell>
    <Table.Cell mono muted className="truncate text-xs">
      {result.primaryEmail ?? '—'}
    </Table.Cell>
    <Table.Cell muted className="text-right">
      {result.memberCount}
    </Table.Cell>
    <Table.Cell>
      <SubscriptionBadge status={result.subscriptionStatus ?? null} />
    </Table.Cell>
  </Table.Row>
);

interface IFamilyPaneProps {
  familyId: string | null;
  isLoading: boolean;
  hasError: boolean;
  onRetry: () => void;
  family: NonNullable<AdminGetFamilyAccountQuery['adminGetFamilyAccount']> | null;
  tab: AccountTab;
  onTabChange: (tab: AccountTab) => void;
}

const FamilyPane = ({ familyId, isLoading, hasError, onRetry, family, tab, onTabChange }: IFamilyPaneProps) => {
  if (familyId === null) {
    return <div className="text-fg-tertiary flex-1 text-xs italic">Pick a household to read it.</div>;
  }
  // Before the null branch: a failed read also yields null, and reporting it as
  // "no household with that id" tells the operator the household is gone.
  if (hasError) {
    return (
      <div className="flex-1">
        <QueryError message="The household failed to load." onRetry={onRetry} />
      </div>
    );
  }
  if (isLoading && family === null) {
    return <div className="text-fg-tertiary flex-1 text-xs italic">Loading household…</div>;
  }
  if (family === null) {
    return (
      <div className="text-fg-tertiary flex-1 text-xs italic">
        No household with id <InspectLink id={familyId} />.
      </div>
    );
  }

  const householdLinks = householdToolLinks(family.linkedUsers);
  const ownerId = householdOwnerId(family.linkedUsers);
  const owner = family.linkedUsers.find((member) => member.id === ownerId);
  const tabCounts: Partial<Record<AccountTab, number>> = {
    users: family.linkedUsers.length,
    banks: family.bankConnections.length,
    workItems: family.openWorkItems.totalReturned,
  };

  return (
    <div className="flex min-w-0 flex-1 flex-col gap-3">
      {/* Above the tabs, so every tab says which household it describes. */}
      <h2 className="text-fg-primary text-base font-semibold">
        {formatHouseholdName(family.name ?? null) ?? <Absent>Unnamed household</Absent>}
      </h2>

      <Tabs.Root selectedKey={tab} onSelectionChange={(key) => onTabChange(parseAccountTab(String(key)))}>
        <Tabs.List
          aria-label="Household sections"
          className="border-border-primary gap-1 overflow-visible text-xs uppercase tracking-wider"
        >
          {ACCOUNT_TABS.map(({ id, label }) => (
            <Tabs.Tab
              key={id}
              id={id}
              className={({ isSelected }) =>
                cx(
                  '-mb-px h-auto px-3 py-2 text-xs font-medium',
                  isSelected
                    ? 'text-fg-brand-primary border-fg-brand-primary'
                    : 'text-fg-secondary hover:text-fg-primary'
                )
              }
            >
              {label}
              {tabCounts[id] !== undefined && (
                <span className="text-fg-tertiary ml-1.5 font-normal">{tabCounts[id]}</span>
              )}
            </Tabs.Tab>
          ))}
        </Tabs.List>

        <Tabs.Panel id="household" className="flex flex-col gap-6 pt-4">
          <FieldList>
            <Field label="Family id" mono>
              <InspectLink id={family.id} />
            </Field>
            <Field label="Created">
              <Timestamp instant={family.createdAt} />
              {owner !== undefined && (
                <span className="text-fg-tertiary"> by {formatPersonName(owner, owner.email)}</span>
              )}
            </Field>
            <Field label="Email endpoint" mono>
              {family.inboundEmailAddress ?? <Absent>None</Absent>}
            </Field>
            <Field label="Members">{family.memberCount}</Field>
            {householdLinks.length > 0 && (
              <Field label="Open in">
                <ToolLinks label="Household tools" links={householdLinks} />
              </Field>
            )}
          </FieldList>
          <DependentsPanel dependents={family.dependents} />
          <PossessionsPanel possessions={family.possessions} />
        </Tabs.Panel>
        <Tabs.Panel id="users" className="pt-4">
          <LinkedUsersPanel linkedUsers={family.linkedUsers} />
        </Tabs.Panel>
        <Tabs.Panel id="banks" className="pt-4">
          <BankConnectionsPanel bankConnections={family.bankConnections} />
        </Tabs.Panel>
        <Tabs.Panel id="billing" className="pt-4">
          <BillingPanel billing={family.billing} />
        </Tabs.Panel>
        <Tabs.Panel id="workItems" className="pt-4">
          <OpenWorkItemsPanel openWorkItems={family.openWorkItems} familyId={family.id} />
        </Tabs.Panel>
      </Tabs.Root>
    </div>
  );
};

interface IQueryErrorProps {
  message: string;
  onRetry: () => void;
}

/** A failed read, told apart from an empty one, with the retry in reach. */
const QueryError = ({ message, onRetry }: IQueryErrorProps) => (
  <div className="border-fg-error-primary flex items-center gap-3 border px-3 py-2">
    <span className="text-fg-error-primary text-xs">{message}</span>
    <Button color="secondary" size="xs" onClick={onRetry}>
      Retry
    </Button>
  </div>
);

export default AccountsScene;
